/**
 * Spec-term tooltips
 * -------------------------------------------------------------------------
 * Annotates recognised technology / standard / protection-class terms inside
 * opted-in containers (`[data-spec-terms]`) with an inline (i) that opens a
 * tooltip carrying the term's description.
 *
 * The dictionary comes from the `spec_term` metaobject, rendered as a JSON
 * payload by `snippets/spec-term-data.liquid`. Nothing here is hardcoded —
 * Bultex edits the entries in Admin → Content → Metaobjects.
 *
 * Three rules that are load-bearing, not stylistic:
 *
 *  1. Match on the metaobject HANDLE, never on `name`. The definition's
 *     `translatable` capability has no per-field opt-out, so a translation app
 *     rewrites `name` per locale; `handle` is the only stable identity.
 *
 *  2. Match over DOM text nodes, never over HTML strings. Once `Kevlar` has
 *     become `<span class="spec-term">Kevlar</span>`, a string replace for the
 *     next term scans that markup too — the class code `A` matches inside
 *     `class=`, `P` inside `span` — and shreds the attributes.
 *
 *  3. Sort the alternation longest-first and use `\p{L}\p{N}` lookarounds, not
 *     `\b`. Ordering is what resolves `S1` / `S1P` / `S1PS`, `EVA` / `EVANIT`
 *     and `TPU` / `PU/TPU`; `\b` is a Latin-word boundary and misfires against
 *     the Cyrillic prose these terms sit inside.
 */

(() => {
  const PAYLOAD_SELECTOR = 'script[data-spec-term-data]';
  const CONTAINER_SELECTOR = '[data-spec-terms]';

  /** Text inside these never gets annotated (already interactive, or not prose). */
  const SKIP_TAGS = new Set([
    'A',
    'BUTTON',
    'SCRIPT',
    'STYLE',
    'TEXTAREA',
    'SELECT',
    'OPTION',
    'CODE',
    'KBD',
    'SVG',
  ]);

  const ICON_SVG =
    '<svg class="spec-term__icon" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="7.1" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
    '<circle cx="8" cy="4.6" r="0.95" fill="currentColor"/>' +
    '<path d="M8 7.1v5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  const supportsPopover =
    typeof HTMLElement !== 'undefined' && HTMLElement.prototype.hasOwnProperty('popover');

  let panelSeq = 0;
  let dictionaryCache = null;
  let labelsCache = null;

  /* ----------------------------------------------------------------- data */

  function readDictionary() {
    if (dictionaryCache) return dictionaryCache;

    const byHandle = new Map();

    // Merge every payload on the page. Normally there is exactly one, but a
    // merchant can place two spec-table sections on the same template.
    document.querySelectorAll(PAYLOAD_SELECTOR).forEach((script) => {
      let entries;
      try {
        entries = JSON.parse(script.textContent);
      } catch (error) {
        console.warn('[spec-term-tooltips] could not parse dictionary payload', error);
        return;
      }
      if (!Array.isArray(entries)) return;

      entries.forEach((entry) => {
        const handle = typeof entry?.handle === 'string' ? entry.handle.trim() : '';
        if (!handle || byHandle.has(handle)) return;
        if (!entry.description) return;
        byHandle.set(handle, entry);
      });
    });

    dictionaryCache = [...byHandle.values()];
    return dictionaryCache;
  }

  function readLabels() {
    if (labelsCache) return labelsCache;
    const script = document.querySelector(PAYLOAD_SELECTOR);
    labelsCache = {
      moreInfo: script?.dataset.labelMoreInfo || '%TERM%',
      close: script?.dataset.labelClose || 'Close',
    };
    return labelsCache;
  }

  /* -------------------------------------------------------------- matching */

  /**
   * Expand a handle back into a tolerant pattern.
   *
   * `handleize` maps `®`, `™`, apostrophes, `/`, `.` and whitespace all to a
   * single `-`, so every `-` was at least one separator in the original name.
   * Re-expanding it to "one or more non-alphanumerics" reconstructs the match
   * form for free: `gore-tex` matches `GORE-TEX®`, `Gore Tex`; `d-cover-dry`
   * matches `D'COVER DRY®`.
   *
   * `+` and not `*` — a `-` is only emitted where a separator existed, so `+`
   * is exact where `*` would over-match (`pu-tpu` must not match bare `TPU`).
   */
  function patternForHandle(handle) {
    return handle
      .split(/-+/)
      .filter(Boolean)
      .map((chunk) => chunk.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('[^\\p{L}\\p{N}]+');
  }

  /**
   * `kind` is a text field the translation app can rewrite, so this fails
   * CLOSED: anything that is not exactly `technology` is treated as
   * class-scoped. A missing tooltip is cheap; `P` firing inside every
   * Bulgarian paragraph is not.
   */
  function isTechnology(entry) {
    return String(entry.kind || '').trim().toLowerCase() === 'technology';
  }

  function buildMatcher(entries) {
    if (!entries.length) return null;

    // Longest-first: JS alternation is leftmost-first, so ordering IS the
    // disambiguation — no precedence or exclusion table needed.
    const sorted = [...entries].sort((a, b) => b.handle.length - a.handle.length);

    const source = sorted
      .map((entry) => `(?<![\\p{L}\\p{N}])(${patternForHandle(entry.handle)})(?![\\p{L}\\p{N}])`)
      .join('|');

    try {
      return { re: new RegExp(source, 'giu'), terms: sorted };
    } catch (error) {
      // Lookbehind is unsupported below Safari 16.4 — degrade to no tooltips
      // rather than to wrong matches.
      console.warn('[spec-term-tooltips] term pattern unsupported in this browser', error);
      return null;
    }
  }

  /* ------------------------------------------------------------ annotation */

  function collectTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        for (let el = node.parentElement; el && el !== root.parentElement; el = el.parentElement) {
          if (SKIP_TAGS.has(el.tagName.toUpperCase())) return NodeFilter.FILTER_REJECT;
          if (el.classList.contains('spec-term')) return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    return nodes;
  }

  function buildTrigger(matchedText, entry) {
    const labels = readLabels();
    const wrapper = document.createElement('span');
    wrapper.className = 'spec-term';
    wrapper.appendChild(document.createTextNode(matchedText));

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'spec-term__trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute(
      'aria-label',
      labels.moreInfo.replace('%TERM%', entry.name || matchedText)
    );
    trigger.innerHTML = ICON_SVG;
    trigger.specTermEntry = entry;

    wrapper.appendChild(trigger);
    return wrapper;
  }

  /** Punctuation that ends the token — never absorbed into the trigger. */
  const TOKEN_STOP = new Set([',', ';', '!', '?', ')', ']', '}', '«', '»', '"', '„', '”', '…']);

  /** `-` `/` `.` `:` continue the token only when a digit follows. */
  const TOKEN_JOINER = new Set(['-', '/', '.', ':']);

  /**
   * Extend a match to the end of its token, so the (i) sits after a trailing ®
   * or a version suffix instead of splitting it: `Kevlar®(i)`, not
   * `Kevlar(i)®`, and `EN ISO 20345:2022+A1:2024(i)`, not
   * `EN ISO 20345(i):2022+A1:2024`.
   *
   * The match itself stops at the last letter or digit — that boundary is what
   * lets `cordura` match `CORDURA®` at all — so the tail is recovered here.
   * It stops at sentence punctuation and at a joiner that introduces a word
   * rather than a version, keeping `EVANIT,` and `EVA-подплата` on the term.
   */
  function extendToTokenEnd(text, start) {
    let end = start;
    while (end < text.length) {
      const char = text[end];
      if (/\s/.test(char) || TOKEN_STOP.has(char)) break;
      if (TOKEN_JOINER.has(char) && !/\p{N}/u.test(text[end + 1] || '')) break;
      end += 1;
    }
    return end;
  }

  function annotateTextNode(node, matcher, seen) {
    const text = node.nodeValue;
    matcher.re.lastIndex = 0;

    let fragment = null;
    let cursor = 0;
    let match;

    while ((match = matcher.re.exec(text)) !== null) {
      const groupIndex = match.slice(1).findIndex((group) => group !== undefined);
      const entry = matcher.terms[groupIndex];
      if (!entry) continue;

      // First occurrence per container only — repeated icons in one row are
      // noise, and it bounds the DOM work.
      if (seen.has(entry.handle)) continue;
      seen.add(entry.handle);

      const end = extendToTokenEnd(text, match.index + match[0].length);

      fragment = fragment || document.createDocumentFragment();
      fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)));
      fragment.appendChild(buildTrigger(text.slice(match.index, end), entry));
      cursor = end;

      // Never scan the absorbed tail — it is already inside a trigger.
      matcher.re.lastIndex = end;
    }

    if (!fragment) return;
    fragment.appendChild(document.createTextNode(text.slice(cursor)));
    node.replaceWith(fragment);
  }

  function annotateContainer(container, matchers) {
    if (container.dataset.specTermsReady === 'true') return;
    container.dataset.specTermsReady = 'true';

    // Only containers explicitly marked as class/standard fields see the short
    // codes (`A`, `E`, `P`, `S1`, `FO`, `HI`, `WR`…). Everything else is
    // technology-only, so those codes cannot fire inside ordinary prose.
    const matcher = container.dataset.specTerms === 'all' ? matchers.all : matchers.technology;
    if (!matcher) return;

    const seen = new Set();
    collectTextNodes(container).forEach((node) => annotateTextNode(node, matcher, seen));
  }

  /* --------------------------------------------------------------- tooltip */

  function buildPanel(entry) {
    const labels = readLabels();
    const panel = document.createElement('div');
    panel.className = 'spec-term__panel';
    panel.id = `SpecTermPanel-${(panelSeq += 1)}`;
    panel.setAttribute('role', 'dialog');
    if (supportsPopover) panel.setAttribute('popover', 'auto');

    if (entry.image) {
      const image = document.createElement('img');
      image.className = 'spec-term__image';
      image.src = entry.image;
      image.alt = '';
      image.loading = 'lazy';
      panel.appendChild(image);
    }

    const body = document.createElement('div');
    body.className = 'spec-term__body';

    if (entry.name) {
      const title = document.createElement('p');
      title.className = 'spec-term__title';
      title.textContent = entry.name;
      body.appendChild(title);
    }

    const description = document.createElement('p');
    description.className = 'spec-term__description';
    description.textContent = entry.description;
    body.appendChild(description);

    panel.appendChild(body);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'spec-term__close';
    close.setAttribute('aria-label', labels.close);
    close.innerHTML =
      '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">' +
      '<path d="M1 1l10 10M11 1L1 11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '</svg>';
    panel.appendChild(close);
    panel.tabIndex = -1;

    // A native popover light-dismisses (outside click, Escape) without going
    // through closeTooltip, so mirror that back into our own state.
    if (supportsPopover) {
      panel.addEventListener('toggle', (event) => {
        if (event.newState === 'closed' && openState.panel === panel) releaseOpenState(panel);
      });
    }

    document.body.appendChild(panel);
    return panel;
  }

  function placePanel(panel, trigger) {
    // Measure unshifted, then clamp into the viewport. The panel is fixed
    // (top layer under `popover`), so nothing in the spec table reflows.
    panel.style.left = '0px';
    panel.style.top = '0px';

    const gap = 10;
    const margin = 12;
    const anchor = trigger.getBoundingClientRect();
    const box = panel.getBoundingClientRect();

    const maxLeft = Math.max(margin, window.innerWidth - box.width - margin);
    const left = Math.min(Math.max(margin, anchor.left + anchor.width / 2 - box.width / 2), maxLeft);

    let top = anchor.top - box.height - gap;
    let placement = 'top';
    if (top < margin) {
      top = anchor.bottom + gap;
      placement = 'bottom';
    }

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
    panel.dataset.placement = placement;
    panel.style.setProperty(
      '--spec-term-caret-x',
      `${Math.round(anchor.left + anchor.width / 2 - left)}px`
    );
  }

  const openState = { trigger: null, panel: null };

  function reposition() {
    if (openState.panel) placePanel(openState.panel, openState.trigger);
  }

  /** Tear down the open-state bookkeeping and hide the panel. Idempotent. */
  function releaseOpenState(panel) {
    const trigger = openState.trigger;
    const focusWasInside = panel.contains(document.activeElement);

    openState.panel = null;
    openState.trigger = null;

    // `[data-open]` is what makes the panel visible — see the note in
    // component-spec-term.css about the UA popover display rule.
    panel.removeAttribute('data-open');
    trigger?.setAttribute('aria-expanded', 'false');
    window.removeEventListener('scroll', reposition, true);
    window.removeEventListener('resize', reposition);

    // Never strand focus on a hidden panel.
    if (focusWasInside) trigger?.focus();
  }

  function closeTooltip({ focusTrigger = false } = {}) {
    const { panel, trigger } = openState;
    if (!panel) return;

    // Hide first, then leave the top layer, so the panel never renders in the
    // normal flow for a frame.
    panel.removeAttribute('data-open');
    if (supportsPopover && panel.matches(':popover-open')) panel.hidePopover();
    releaseOpenState(panel);

    if (focusTrigger) trigger.focus();
  }

  function openTooltip(trigger) {
    const entry = trigger.specTermEntry;
    if (!entry) return;

    closeTooltip();

    const panel = trigger.specTermPanel || (trigger.specTermPanel = buildPanel(entry));
    openState.trigger = trigger;
    openState.panel = panel;

    // Must be visible before showPopover(), otherwise it enters the top layer
    // still `display: none`.
    panel.setAttribute('data-open', 'true');
    if (supportsPopover) panel.showPopover();

    trigger.setAttribute('aria-expanded', 'true');
    placePanel(panel, trigger);

    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
  }

  function onDocumentClick(event) {
    const trigger = event.target.closest?.('.spec-term__trigger');
    if (trigger) {
      event.preventDefault();
      if (openState.trigger === trigger) closeTooltip({ focusTrigger: true });
      else openTooltip(trigger);
      return;
    }

    if (event.target.closest?.('.spec-term__close')) {
      closeTooltip({ focusTrigger: true });
      return;
    }

    // Native popover light-dismisses itself; the fallback needs this.
    if (!supportsPopover && openState.panel && !event.target.closest?.('.spec-term__panel')) {
      closeTooltip();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && openState.panel) closeTooltip({ focusTrigger: true });
  }

  /* ------------------------------------------------------------------ init */

  function init(scope = document) {
    const containers = scope.querySelectorAll(CONTAINER_SELECTOR);
    if (!containers.length) return;

    const entries = readDictionary();
    if (!entries.length) return;

    const matchers = {
      all: buildMatcher(entries),
      technology: buildMatcher(entries.filter(isTechnology)),
    };

    containers.forEach((container) => annotateContainer(container, matchers));
  }

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders a section's HTML in place.
  document.addEventListener('shopify:section:load', (event) => {
    dictionaryCache = null;
    labelsCache = null;
    init(event.target);
  });
})();
